<template>
	<div class="repo-card-list-wrapper">
		<!-- No-results empty state (optionally animated) -->
		<transition name="noResultBannerTransition">
			<EmptyState
				v-if="showNoResultsBanner && animateEmptyState"
				:body="noResultsText"
			/>
		</transition>
		<EmptyState
			v-if="showNoResultsBanner && !animateEmptyState"
			:body="noResultsText"
		/>

		<!-- Repo cards + end-of-list marker -->
		<transition-group
			name="repositoryCardsListTransition"
			tag="div"
			mode="out-in"
			class="repo-card-list"
		>
			<template v-if="!showNoResultsBanner">
				<AddRepositoryCard
					v-for="repo in displayRepoList"
					:key="repo.id"
					:initialRepoData="repo"
					@repo-connection-request="$emit('repo-connection-request', $event)"
					@repository-disconnected="$emit('repository-disconnected', $event)"
				/>
				<EndOfRepoListMarker
					v-if="endOfRepoList"
					key="endOfResultsBanner"
				/>
			</template>
			<template v-if="loadingRepos">
				<AddRepositoryCard
					v-for="n in 3"
					:key="`${skeletonKeyPrefix}-${n}`"
				/>
			</template>
		</transition-group>

		<!-- Infinite-scroll trigger -->
		<IntersectionObserver
			v-if="!endOfRepoList"
			@intersect-enter="$emit('load-next-page')"
		/>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Endpoints } from "@octokit/types";
import { RepoConnectionConfig } from "@/types/general";

// Components
import AddRepositoryCard from "@/components/Repositories/AddRepositoryCard.vue";
import EndOfRepoListMarker from "@/components/Repositories/EndOfRepoListMarker.vue";
import IntersectionObserver from "@/components/IntersectionObserver.vue";
import EmptyState from "@/components/Common/EmptyState.vue";

type RepoList = Endpoints["GET /user/repos"]["response"]["data"];

export default defineComponent({
	name: "RepoCardList",
	components: { AddRepositoryCard, EndOfRepoListMarker, IntersectionObserver, EmptyState },
	props: {
		/** Repositories to render as cards. */
		displayRepoList: { type: Array as PropType<RepoList>, required: true },
		/** When true the no-results EmptyState is shown. */
		showNoResultsBanner: { type: Boolean, required: true },
		/** Body text for the no-results EmptyState. */
		noResultsText: { type: String, required: true },
		/** Whether the repo list is currently loading (shows skeleton cards). */
		loadingRepos: { type: Boolean, required: true },
		/** Whether the full list has been fetched (hides IntersectionObserver). */
		endOfRepoList: { type: Boolean, required: true },
		/** Key prefix for skeleton AddRepositoryCard instances (e.g. "list-skeleton" or "search-skeleton"). */
		skeletonKeyPrefix: { type: String, required: true },
		/** Wrap the no-results EmptyState in a CSS transition. */
		animateEmptyState: { type: Boolean, default: false },
	},
	emits: {
		"repo-connection-request": (_data: RepoConnectionConfig) => true,
		"repository-disconnected": (_data: { repositoryID: string }) => true,
		/** Fired when the IntersectionObserver sentinel enters the viewport. */
		"load-next-page": () => true,
	},
});
</script>

<style lang="scss" scoped>
.repo-card-list-wrapper {
	display: contents;
}

/* repositoryCardsListTransition */
.repositoryCardsListTransition-enter-active,
.repositoryCardsListTransition-leave-active {
	transition: all var(--sgnl-motion-duration-moderate-02) ease;
}

.repositoryCardsListTransition-enter {
	opacity: 0;
	transform: translateY(30px);
	transition-duration: var(--sgnl-motion-duration-moderate-02);
}

.repositoryCardsListTransition-leave-to {
	opacity: 0;
	transition-duration: var(--sgnl-motion-duration-moderate-02);
}

/* noResultBannerTransition (used when animateEmptyState = true) */
.noResultBannerTransition-enter-active,
.noResultBannerTransition-leave-active {
	transition: all var(--sgnl-motion-duration-moderate-02) ease;
}

.noResultBannerTransition-enter {
	transform: scale(1.05, 1.05);
	transition-duration: var(--sgnl-motion-duration-moderate-02);
}

.noResultBannerTransition-leave-to {
	opacity: 0;
	transform: scale(1, 1);
	transition-duration: 0ms;
}
</style>
